// src/pages/AddItemSubCategory.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import EditItemCategoryModal from "../components/addItemEditModal";
import { hasPermission } from "../utils/permissionHelper";
import BulkItemSubCategoryUpload from "../components/bulkItemSubCategoryUpload";
import Access from "../components/access";

const API = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";
const PAGE_SIZE = 10;

export default function AddItemSubCategory() {
  useAuthRedirect();

  const { catCode, itemName } = useParams();
  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");

  const [formData, setFormData] = useState({ subCatCode: "", catName: "" });

  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [q, setQ]           = useState("");
  const [loading, setLoading] = useState(false);

  const canView  = hasPermission("Item Code Generation", "view");
  const canEntry = hasPermission("Item Code Generation", "entry");
  const canEdit  = hasPermission("Item Code Generation", "edit");
  const canDel   = hasPermission("Item Code Generation", "del");

  const [bulkOpen, setBulkOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: "", catName: "" });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const indexOfFirstItem = (page - 1) * PAGE_SIZE;

  // fetch next subcategory code
  const fetchNextSubCategoryCode = useCallback(async () => {
    if (!catCode) return;
    try {
      const resp = await axios.get(`${API}/itemSubCat_no`, { params: { catCode } });
      const sc = resp.data?.newCode;
      if (!sc) throw new Error(resp.data?.error || "No code returned");
      setFormData((p) => ({ ...p, subCatCode: sc }));
    } catch (err) {
      console.error("Error fetching subcategory code:", err);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to get next Sub Category code." });
    }
  }, [catCode]);

  // fetch page
  const fetchPage = useCallback(async (p, showLoader = true) => {
    if (!catCode) return;
    try {
      if (showLoader) setLoading(true);
      const { data } = await axios.get(`${API}/itemSubCat_list`, {
        params: { catCode, page: p, pageSize: PAGE_SIZE, q }
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      console.error("Error fetching list:", err);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load list." });
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [catCode, q]);

  // on mount
  useEffect(() => {
    if (!catCode) return;
    (async () => {
      await fetchNextSubCategoryCode();
      await fetchPage(1);
    })();
  }, [catCode, fetchNextSubCategoryCode, fetchPage]);

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => { fetchPage(1); }, 400);
    return () => clearTimeout(id);
  }, [q, fetchPage]);

  const refreshAfterCommit = async () => {
    await fetchPage(1);
    await fetchNextSubCategoryCode();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.catName || !formData.catName.trim()) {
      return Swal.fire({ icon: "warning", title: "Empty Field", text: "Please enter a valid Sub category name!" });
    }

    const payload = {
      branch,
      user: userId,
      mainCode: catCode,
      subCode: formData.subCatCode,
      name: formData.catName.trim(),
    };

    try {
      Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const resp = await axios.post(`${API}/itemSubCat_insert`, payload);
      if (resp.data.success) {
        Swal.fire({ icon: "success", title: "Success", text: "Sub Category added successfully!" });
        setFormData((prev) => ({ ...prev, catName: "" }));
        await refreshAfterCommit();
      } else {
        Swal.fire({ icon: "error", title: "Error", text: resp.data.message || "Submission failed." });
      }
    } catch (err) {
      console.error("Submit error:", err);
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Submission failed." });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        await axios.delete(`${API}/itemSubCat_delete/${id}`);
        Swal.fire({ title: "Deleted!", text: "Your record has been deleted.", icon: "success" });
        await fetchPage(page);
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire({ title: "Error!", text: "Failed to delete category.", icon: "error" });
      }
    });
  };

  const openEditModal = (item) => {
    setEditFormData({ id: item.id, catNo: item.code, catName: item.itemName });
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const goTo = (p) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    fetchPage(clamped);
  };

  if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Item Sub Category</h2>
            <Link to="/inventory/add_Item_Category">
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {canEntry && (
            <button className="submit-btn" type="button" onClick={() => setBulkOpen(true)}>
              CSV Upload
            </button>
          )}
        </div>

        <div className="form-container">
          {canEntry && (
            <form className="vehicle-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Category Code :</label>
                  <input type="text" value={catCode || ""} readOnly style={{ background: "#f8c9bf", fontWeight: "bold" }} />
                </div>
                <div className="form-group">
                  <label>Category Name :</label>
                  <input type="text" value={itemName || ""} readOnly style={{ background: "#f8c9bf", fontWeight: "bold" }} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sub Category Code :</label>
                  <input type="text" name="subCatCode" value={formData.subCatCode || ""} readOnly />
                </div>
                <div className="form-group">
                  <label>Sub Category Name :</label>
                  <input type="text" name="catName" value={formData.catName || ""} onChange={handleChange} placeholder="Enter Sub Cat Name" />
                </div>
              </div>

              <div className="btn-cls">
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          )}

          {/* Search */}
          <div className="search-filter" style={{ marginTop: 12 }}>
            <p>Search: </p>
            <input
              type="text"
              placeholder="Search by Category Listing.."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="search-input"
            />
            <span style={{ marginLeft: 10, opacity: .7 }}>Total: {total}</span>
          </div>

          {/* Listing */}
          <div className="content-table" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Category Listing</th>
                  {canEdit && <th>Edit</th>}
                  <th>Inner Sub</th>
                  {canDel && <th>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="loading-text">Loading...</td></tr>
                ) : items.length > 0 ? (
                  items.map((item, idx) => (
                    <tr key={item.id ?? `${item.code}-${idx}`}>
                      <td>{indexOfFirstItem + idx + 1}</td>
                      <td>{item.code} - ({item.itemName})</td>
                      {canEdit && (
                        <td>
                          <button
                            style={{ backgroundColor: "transparent", border: "none", color: "blue", cursor: "pointer" }}
                            title="Edit"
                            onClick={() => openEditModal(item)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </td>
                      )}
                      <td>
                        <Link to={`/inventory/add_Inner_Subcategory/${catCode}/${itemName}/${item.code}/${item.itemName}`}>
                          Click
                        </Link>
                      </td>
                      {canDel && (
                        <td className="del">
                          {item.childCount > 0 ? "No Access" : (
                            <i className="fa-solid fa-trash-can" onClick={() => handleDelete(item.id)}></i>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No Data Available</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination" style={{ marginTop: 12 }}>
            <button onClick={() => goTo(page - 1)} disabled={page === 1}>Previous</button>
            {(() => {
              const windowSize = 2;
              let start = Math.max(2, page - windowSize);
              let end = Math.min(totalPages - 1, page + windowSize);
              const buttons = [];
              // always show first
              buttons.push(
                <button key={1} onClick={() => goTo(1)} className={page === 1 ? "active" : ""}>1</button>
              );
              if (start > 2) buttons.push(<span key="dots-left">…</span>);
              for (let p = start; p <= end; p++) {
                buttons.push(
                  <button key={p} onClick={() => goTo(p)} className={page === p ? "active" : ""}>{p}</button>
                );
              }
              if (end < totalPages - 1) buttons.push(<span key="dots-right">…</span>);
              if (totalPages > 1) {
                buttons.push(
                  <button key={totalPages} onClick={() => goTo(totalPages)} className={page === totalPages ? "active" : ""}>{totalPages}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => goTo(page + 1)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </div>

      <EditItemCategoryModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        fetchData={() => fetchPage(page)}
      />

      <BulkItemSubCategoryUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCommitted={refreshAfterCommit}
        catCode={catCode}
        catName={itemName}
      />
    </div>
  );
}
